// LE PLAN DE MÉTRO, généré au changement de morceau dans un Web Worker (R17)
// pour visualizers/metro.js. Une seule fonction autonome (sans import, sans
// fermeture) : le worker la reçoit par sa source (generateMetro.toString()).
//
// L'ALGORITHME (plan octolinéaire sur grille) :
//  1. Grille de pas P en unités de hauteur (y de -1 à 1). Un nœud est
//     interdit s'il tombe hors du cadre moins sa marge (5 % + la portée d'une
//     station), dans la zone de sécurité de la pochette (+ marge), ou dans
//     une zone d'exclusion de l'interface (+ portée).
//  2. CORRESPONDANCES : H = 5 ou 6 nœuds sur un anneau autour de la pochette,
//     répartis en angle (angles pris dans le carré du cadre, x / aspect ; un
//     cadre large a toujours une correspondance face à chaque côté). La
//     ligne i passe par deux correspondances voisines (i, i+1), si bien que
//     chacune en reçoit au moins deux.
//  3. Chaque ligne : correspondance A -> correspondance B, puis de B vers la
//     SORTIE la plus proche (un nœud du bord du cadre) dans un secteur de
//     25° autour du rayon pochette -> B (puis 45°, puis tout le cadre), puis
//     de A vers une autre sortie, en sens inverse. A* sur des ÉTATS (nœud,
//     direction, pas depuis le dernier virage) : 8 directions, virages de 45
//     ou 90° seulement (jamais 135°), au moins deux pas droits entre deux
//     virages (l'arrondi de rayon fixe y tient), 45° au plus à une
//     correspondance (la station reste sur la ligne). Une arête déjà prise
//     par une ligne est interdite aux autres : deux lignes qui vont dans le
//     même sens courent sur des couloirs voisins, à un pas de grille - le
//     « décalage » des tronçons parallèles. Un tracé qui tourne de 180° en
//     14 pas (une épingle) est refusé tant qu'une autre paire de
//     correspondances le permet. Une sortie fixée d'avance faisait boucler
//     la ligne quand elle tombait derrière son cap.
//  4. Les sommets deviennent des segments et des ARCS de rayon fixe R = 0,8 P
//     (tangence : R tan(θ/2) <= 0,8 P <= la moitié du plus court segment).
//  5. Stations : terminus, correspondances, et un nœud droit tous les 4 à 6
//     pas qu'aucune autre ligne ne traverse.
//  6. Pour le rendu : les primitives (8 flottants chacune), une grille de
//     cellules qui liste les primitives proches de chaque cellule (le quad
//     plein écran ne teste que celles-là), et le tracé échantillonné tous les
//     DS pour placer les rames à abscisse curviligne.
export function generateMetro(o) {
  var TAU = Math.PI * 2;
  var seed = (o.seed >>> 0) || 1;
  function rnd() { seed = (seed + 0x6D2B79F5) >>> 0; var t = seed; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }
  var A = o.aspect, unit = 2 / o.screenH;
  var P = Math.max(o.pitchMin, o.pitchPx * unit);
  var reach = o.reachPx * unit;
  var M = o.edgeMargin + reach;
  var cx = o.safeCenter[0], cy = o.safeCenter[1];
  var bx = o.safeHalf[0] + o.coverMargin + reach, by = o.safeHalf[1] + o.coverMargin + reach;
  var NX = Math.floor((2 * (A - M)) / P) + 1, NY = Math.floor((2 * (1 - M)) / P) + 1;
  var X0 = -((NX - 1) * P) / 2, Y0 = -((NY - 1) * P) / 2;
  var N = NX * NY;
  var DX = [1, 1, 0, -1, -1, -1, 0, 1], DY = [0, 1, 1, 1, 0, -1, -1, -1];
  function px(n) { return X0 + (n % NX) * P; }
  function py(n) { return Y0 + Math.floor(n / NX) * P; }
  function blockedPoint(x, y) {
    if (Math.abs(x) > A - M + 1e-9 || Math.abs(y) > 1 - M + 1e-9) return true;
    if (Math.abs(x - cx) < bx && Math.abs(y - cy) < by) return true;
    for (var k = 0; k < o.exclusions.length; k++) {
      var e = o.exclusions[k], w = e[1] * 2 * A, h = e[2] * 2;
      var x0 = e[0].charAt(1) === 'r' ? A - w : -A, x1 = e[0].charAt(1) === 'r' ? A : -A + w;
      var y0 = e[0].charAt(0) === 't' ? 1 - h : -1, y1 = e[0].charAt(0) === 't' ? 1 : -1 + h;
      if (x > x0 - reach && x < x1 + reach && y > y0 - reach && y < y1 + reach) return true;
    }
    return false;
  }
  var blocked = new Uint8Array(N);
  for (var n0 = 0; n0 < N; n0++) blocked[n0] = blockedPoint(px(n0), py(n0)) ? 1 : 0;
  function edgeOk(a, b) {
    if (blocked[a] || blocked[b]) return false;
    var ax = px(a), ay = py(a), bxx = px(b), byy = py(b);
    for (var q = 1; q < 4; q++) if (blockedPoint(ax + (bxx - ax) * q / 4, ay + (byy - ay) * q / 4)) return false;
    return true;
  }
  // edge validity, computed once per (node, direction): A* asked it for every
  // expansion, three sample points each (most of the time on the kiosk)
  var edgeCache = new Int8Array(N * 8).fill(-1);
  function edgeOkDir(n, d, m) {
    var k = n * 8 + d;
    if (edgeCache[k] < 0) edgeCache[k] = edgeOk(n, m) ? 1 : 0;
    return edgeCache[k] === 1;
  }
  function neighbour(n, d) {
    var i = n % NX + DX[d], j = Math.floor(n / NX) + DY[d];
    if (i < 0 || j < 0 || i >= NX || j >= NY) return -1;
    return j * NX + i;
  }
  // the edges taken, per (node, direction), both ways: a typed array (string
  // keys in an object were most of the generation time on the kiosk)
  var usedEdge = new Int16Array(N * 8).fill(-1), usedNode = new Int16Array(N).fill(-1), ownNode = new Uint8Array(N);
  function dirOf(a, b) { var dx = (b % NX) - (a % NX), dy = Math.floor(b / NX) - Math.floor(a / NX); for (var d = 0; d < 8; d++) if (DX[d] === dx && DY[d] === dy) return d; return -1; }
  function setEdge(a, b, v) { var d = dirOf(a, b); usedEdge[a * 8 + d] = v; usedEdge[b * 8 + ((d + 4) % 8)] = v; }

  // --- the interchanges: a ring around the cover --------------------------
  function boxDist(x, y) { var qx = Math.abs(x - cx) - bx, qy = Math.abs(y - cy) - by; return Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) + Math.min(Math.max(qx, qy), 0); }
  var ring = [];
  for (var n1 = 0; n1 < N; n1++) {
    if (blocked[n1]) continue;
    var dd = boxDist(px(n1), py(n1));
    var ok8 = true;
    for (var d8 = 0; d8 < 8; d8++) { var nb = neighbour(n1, d8); if (nb < 0 || blocked[nb]) { ok8 = false; break; } }
    if (ok8 && dd > 1.2 * P && dd < 4.5 * P) ring.push(n1);
  }
  var H = o.lines >= 6 ? 6 : 5;
  var hubs = [];
  // angles in the frame's own square (x / aspect); a wide frame always gets
  // a hub facing each side (th0 near 0), or its sides stay empty
  var th0 = A > 1.2 ? (rnd() - 0.5) * 0.35 : rnd() * TAU;
  for (var k = 0; k < H && ring.length; k++) {
    var want = th0 + TAU * k / H, best = -1, bestS = 1e9;
    for (var r = 0; r < ring.length; r++) {
      var nn = ring[r], ang = Math.atan2(py(nn) - cy, (px(nn) - cx) / A);
      var da = Math.abs(Math.atan2(Math.sin(ang - want), Math.cos(ang - want)));
      var far = 1e9;
      for (var h0 = 0; h0 < hubs.length; h0++) far = Math.min(far, Math.hypot(px(nn) - px(hubs[h0]), py(nn) - py(hubs[h0])));
      if (far < 3 * P) continue;
      var sc = da + 0.15 * Math.abs(boxDist(px(nn), py(nn)) - 2.5 * P) / P;
      if (sc < bestS) { bestS = sc; best = nn; }
    }
    if (best >= 0) hubs.push(best);
  }
  // in angular order round the cover
  hubs.sort(function (a, b) { return Math.atan2(py(a) - cy, (px(a) - cx) / A) - Math.atan2(py(b) - cy, (px(b) - cx) / A); });
  H = hubs.length;

  // --- the edge nodes: where a line enters and leaves the frame ------------
  function edgeNodeToward(hub, jitter) {
    // the allowed node nearest the frame edge along the ray from the cover
    // through the hub
    var hx = px(hub), hy = py(hub), dx = hx - cx, dy = hy - cy, L = Math.hypot(dx, dy) || 1;
    dx /= L; dy /= L;
    var bestN = -1, bestV = -1e9;
    for (var n = 0; n < N; n++) {
      if (blocked[n] || usedNode[n] >= 0) continue;
      var i = n % NX, j = Math.floor(n / NX);
      var onEdge = i === 0 || j === 0 || i === NX - 1 || j === NY - 1;
      if (!onEdge) {
        // also an allowed node whose outward neighbour is blocked by the frame margin
        var out = false;
        for (var d = 0; d < 8; d += 2) { var m = neighbour(n, d); if (m >= 0 && blocked[m] && !blockedByCover(m)) { var mx = px(m), my = py(m); if (Math.abs(mx) > A - M - 1e-9 || Math.abs(my) > 1 - M - 1e-9) out = true; } }
        if (!out) continue;
      }
      var vx = px(n) - cx, vy = py(n) - cy;
      var along = vx * dx + vy * dy, side = Math.abs(vx * dy - vy * dx);
      var v = along - 1.6 * side + rnd() * (0.3 + jitter) * P;
      if (v > bestV) { bestV = v; bestN = n; }
    }
    return bestN;
  }
  function blockedByCover(n) { return Math.abs(px(n) - cx) < bx && Math.abs(py(n) - cy) < by; }
  // the EXITS: allowed nodes on the frame's margin (a line ends at the
  // nearest one it can reach, never at a point fixed in advance: a fixed end
  // behind the line's heading made it loop back on itself)
  var isExit = new Uint8Array(N);
  for (var nx0 = 0; nx0 < N; nx0++) {
    if (blocked[nx0]) continue;
    var ix = nx0 % NX, jx = Math.floor(nx0 / NX);
    var ex = ix === 0 || jx === 0 || ix === NX - 1 || jx === NY - 1;
    for (var dx0 = 0; dx0 < 8 && !ex; dx0 += 2) { var mx0 = neighbour(nx0, dx0); if (mx0 >= 0 && blocked[mx0] && !blockedByCover(mx0) && (Math.abs(px(mx0)) > A - M - 1e-9 || Math.abs(py(mx0)) > 1 - M - 1e-9)) ex = true; }
    isExit[nx0] = ex ? 1 : 0;
  }
  // and only OUTWARD: the exits within 25 degrees of the ray from the cover
  // through the hub, then 45, then any, angles measured in the frame's own
  // square (x / aspect): the nearest exit alone sent every line to the top
  // and bottom edges and the sides of a 16:9 frame stayed empty
  var exitMask = new Uint8Array(N), SECTORS = [0.44, 0.79, 4];
  function setExitSector(hub, half) {
    var a0 = Math.atan2(py(hub) - cy, (px(hub) - cx) / A);
    for (var n = 0; n < N; n++) {
      if (!isExit[n]) { exitMask[n] = 0; continue; }
      if (half >= 3.2) { exitMask[n] = 1; continue; }
      var a = Math.atan2(py(n) - cy, (px(n) - cx) / A), da = Math.abs(Math.atan2(Math.sin(a - a0), Math.cos(a - a0)));
      exitMask[n] = da <= half ? 1 : 0;
    }
  }

  // --- A* over (node, direction, run) ---------------------------------------
  var aWork = 0;
  // goal -1: any free exit. startDir >= 0: the first move turns at most
  // firstTurn steps of 45 degrees (1 at an interchange: its station stays on
  // the line, the arc's offset is 0.07 P)
  function astar(start, startDir, firstTurn, goal, lineId) {
    var S = N * 8 * 3;
    var g = new Float32Array(S).fill(1e9), from = new Int32Array(S).fill(-1);
    var heap = [], hk = [];
    function push(s, f) { heap.push(f); hk.push(s); var i = heap.length - 1; while (i > 0) { var p = (i - 1) >> 1; if (heap[p] <= heap[i]) break; var t = heap[p]; heap[p] = heap[i]; heap[i] = t; t = hk[p]; hk[p] = hk[i]; hk[i] = t; i = p; } }
    function pop() { var top = hk[0], last = heap.length - 1; heap[0] = heap[last]; hk[0] = hk[last]; heap.pop(); hk.pop(); var i = 0; for (;;) { var l = 2 * i + 1, r = l + 1, m = i; if (l < heap.length && heap[l] < heap[m]) m = l; if (r < heap.length && heap[r] < heap[m]) m = r; if (m === i) break; var t = heap[m]; heap[m] = heap[i]; heap[i] = t; t = hk[m]; hk[m] = hk[i]; hk[i] = t; i = m; } return top; }
    var gx = goal % NX, gy = Math.floor(goal / NX);
    function hcost(n) {
      var i = n % NX, j = Math.floor(n / NX);
      if (goal < 0) return Math.min(i, NX - 1 - i, j, NY - 1 - j);
      var ddx = Math.abs(i - gx), ddy = Math.abs(j - gy); return Math.max(ddx, ddy) + 0.414 * Math.min(ddx, ddy);
    }
    function isGoal(n) { return goal < 0 ? (exitMask[n] === 1 && usedNode[n] < 0 && n !== start) : n === goal; }
    var s0 = (start * 8 + Math.max(0, startDir)) * 3 + 2;
    g[s0] = 0; push(s0, hcost(start));
    var free = startDir < 0;
    var visits = 0;
    while (heap.length && visits < 40000) {
      var s = pop(); visits++; aWork++;
      var n = Math.floor(s / 24), dir = Math.floor(s / 3) % 8, run = s % 3;
      if (isGoal(n)) {
        var path = [];
        for (var c = s; c >= 0; c = from[c]) path.push(c);
        path.reverse();
        return path.map(function (st) { return { n: Math.floor(st / 24), d: Math.floor(st / 3) % 8 }; });
      }
      var gs = g[s];
      for (var t = -2; t <= 2; t++) {
        var nd = (dir + t + 8) % 8;
        var first = free && s === s0;
        if (s === s0 && !free && Math.abs(t) > firstTurn) continue;
        if (!first && t !== 0 && run < 2) continue;          // two straight steps between turns
        var m2 = neighbour(n, nd);
        if (m2 < 0 || !edgeOkDir(n, nd, m2)) continue;
        if (usedEdge[n * 8 + nd] >= 0) continue;              // no shared track
        // lines may CROSS (at a node, or diagonals in the middle of a cell);
        // they never share an edge
        if (ownNode[m2]) continue;                            // a line never crosses itself
        if (goal < 0 && usedNode[m2] >= 0 && isExit[m2]) continue;   // an exit someone else ends at
        var cost = (nd % 2 === 1 ? 1.414 : 1) + (first ? 0 : (Math.abs(t) === 1 ? 0.6 : (Math.abs(t) === 2 ? 1.6 : 0)));
        var nrun = first ? 2 : (t === 0 ? Math.min(2, run + 1) : 1);
        var ns = (m2 * 8 + nd) * 3 + nrun;
        var ng = gs + cost;
        if (ng < g[ns]) { g[ns] = ng; from[ns] = s; push(ns, ng + hcost(m2)); }
      }
    }
    return null;
  }

  var lines = [];
  var nLines = o.lines, lenientUsed = 0;
  function markOwn(path, v) { for (var q = 0; q < path.length; q++) ownNode[path[q].n] = v; }
  for (var li = 0; li < nLines; li++) {
    // the hub pairs to try: neighbours first, then one apart, then any
    var pairs = [];
    var base = li % H;
    pairs.push([base, (base + (li < H ? 1 : 2)) % H]);
    for (var pk = 1; pk < H; pk++) pairs.push([(base + pk) % H, (base + pk + 1) % H], [(base + pk) % H, (base + pk + 2) % H]);
    var route = null, usedPair = null;
    // first pass: no hairpin (more than 180 degrees of turning within 14
    // steps: the line doubles back near a hub); second pass: anything
    for (var tp = 0; tp < 2 * pairs.length && !route; tp++) {
      var strict = tp < pairs.length;
      var ha = hubs[pairs[tp % pairs.length][0]], hb = hubs[pairs[tp % pairs.length][1]];
      if (ha === hb) continue;
      // hub A -> hub B first, then on from B to the nearest exit, then back
      // from A to another exit; at each hub the line turns 45 degrees at most
      ownNode.fill(0);
      var leg2 = astar(ha, -1, 2, hb, li);
      if (!leg2 || leg2.length < 2) continue;
      mark(leg2, li); markOwn(leg2, 1);
      ownNode[ha] = 0; ownNode[hb] = 0;
      var leg3 = null;
      for (var sk = 0; sk < SECTORS.length && !leg3; sk++) { setExitSector(hb, SECTORS[sk]); leg3 = astar(hb, leg2[leg2.length - 1].d, 1, -1, li); }
      if (!leg3) { unmark(leg2); continue; }
      mark(leg3, li); markOwn(leg3, 1); ownNode[ha] = 0;
      var back = null;
      for (var sb = 0; sb < SECTORS.length && !back; sb++) { setExitSector(ha, SECTORS[sb]); back = astar(ha, (leg2[1].d + 4) % 8, 1, -1, li); }
      if (!back) { unmark(leg2); unmark(leg3); continue; }
      mark(back, li);
      // the way back, reversed: node k's direction is the move INTO it
      var leg1 = [];
      for (var qb = back.length - 1; qb >= 0; qb--) {
        var dIn = qb < back.length - 1 ? (back[qb + 1].d + 4) % 8 : (back[back.length - 1].d + 4) % 8;
        leg1.push({ n: back[qb].n, d: dIn });
      }
      var cand = leg1.concat(leg2.slice(1), leg3.slice(1));
      if (strict && hairpin(cand)) { unmark(back); unmark(leg2); unmark(leg3); continue; }
      route = cand;
      if (!strict) lenientUsed++;
      usedPair = [ha, hb];
    }
    ownNode.fill(0);
    if (!route) continue;
    for (var q2 = 0; q2 < route.length; q2++) if (usedNode[route[q2].n] < 0) usedNode[route[q2].n] = li;
    lines.push({ id: li, route: route, hubs: usedPair });
  }
  function hairpin(rt) {
    var turns = [];
    for (var q = 2; q < rt.length; q++) { var t = ((rt[q].d - rt[q - 1].d + 12) % 8) - 4; turns.push(t); }
    for (var a = 0; a < turns.length; a++) {
      var sum = 0;
      for (var b = a; b < Math.min(turns.length, a + 14); b++) { sum += turns[b]; if (Math.abs(sum) >= 4) return true; }
    }
    return false;
  }
  function mark(path, id) { for (var q = 1; q < path.length; q++) setEdge(path[q - 1].n, path[q].n, id); }
  function unmark(path) { for (var q = 1; q < path.length; q++) setEdge(path[q - 1].n, path[q].n, -1); }

  // --- geometry: segments and rounded corners -------------------------------
  var R = 0.8 * P;
  var prims = [];         // [type, line, a, b, c, d, e, s0, len] -> packed later
  var outLines = [];
  for (var L1 = 0; L1 < lines.length; L1++) {
    var rt = lines[L1].route;
    // the vertices: nodes where the direction changes (the move INTO the next node)
    var pts = [[px(rt[0].n), py(rt[0].n)]], dirs = [];
    for (var q3 = 1; q3 < rt.length; q3++) {
      var dprev = q3 > 1 ? rt[q3 - 1].d : rt[1].d;
      if (q3 > 1 && rt[q3].d !== rt[q3 - 1].d) pts.push([px(rt[q3 - 1].n), py(rt[q3 - 1].n)]);
      void dprev;
    }
    pts.push([px(rt[rt.length - 1].n), py(rt[rt.length - 1].n)]);
    // rounded corners
    var s = 0, lp = [], start = pts[0];
    for (var v = 1; v < pts.length; v++) {
      var a0 = pts[v - 1], b0 = pts[v];
      var dx1 = b0[0] - a0[0], dy1 = b0[1] - a0[1], l1 = Math.hypot(dx1, dy1); dx1 /= l1; dy1 /= l1;
      if (v < pts.length - 1) {
        var c0 = pts[v + 1];
        var dx2 = c0[0] - b0[0], dy2 = c0[1] - b0[1], l2 = Math.hypot(dx2, dy2); dx2 /= l2; dy2 /= l2;
        var cr = dx1 * dy2 - dy1 * dx2, dt = Math.max(-1, Math.min(1, dx1 * dx2 + dy1 * dy2));
        var theta = Math.acos(dt), tl = R * Math.tan(theta / 2);
        var p1 = [b0[0] - dx1 * tl, b0[1] - dy1 * tl], p2 = [b0[0] + dx2 * tl, b0[1] + dy2 * tl];
        var segLen = Math.hypot(p1[0] - start[0], p1[1] - start[1]);
        if (segLen > 1e-6) { prims.push([0, L1, start[0], start[1], p1[0], p1[1], 0, s, segLen]); s += segLen; }
        var sg = cr > 0 ? 1 : -1;
        var ccx = p1[0] - dy1 * R * sg, ccy = p1[1] + dx1 * R * sg;
        var ang0 = Math.atan2(p1[1] - ccy, p1[0] - ccx);
        var arcLen = R * theta;
        prims.push([1, L1, ccx, ccy, R, ang0, sg * theta, s, arcLen]); s += arcLen;
        start = p2;
      } else {
        var last = Math.hypot(b0[0] - start[0], b0[1] - start[1]);
        prims.push([0, L1, start[0], start[1], b0[0], b0[1], 0, s, last]); s += last;
      }
    }
    outLines.push({ id: lines[L1].id, len: s, pts: pts, route: rt, hubs: lines[L1].hubs });
  }

  // --- the path, sampled every DS (for the trains) -------------------------
  var DS = o.ds;
  function pointAt(line, sAt, out) {
    // the prim that contains sAt (linear scan per line is fine: called at build)
    for (var k2 = 0; k2 < prims.length; k2++) {
      var pr = prims[k2];
      if (pr[1] !== line || sAt > pr[7] + pr[8] + 1e-9) continue;
      var u = Math.max(0, sAt - pr[7]);
      if (pr[0] === 0) { var f = pr[8] > 0 ? u / pr[8] : 0; out[0] = pr[2] + (pr[4] - pr[2]) * f; out[1] = pr[3] + (pr[5] - pr[3]) * f; }
      else { var ang = pr[5] + (pr[6] > 0 ? 1 : -1) * u / pr[4]; out[0] = pr[2] + pr[4] * Math.cos(ang); out[1] = pr[3] + pr[4] * Math.sin(ang); }
      return out;
    }
    return out;
  }
  var samples = [];
  for (var L2 = 0; L2 < outLines.length; L2++) {
    var cnt = Math.ceil(outLines[L2].len / DS) + 1, arr = new Float32Array(cnt * 2), tmp = [0, 0];
    for (var i2 = 0; i2 < cnt; i2++) { pointAt(L2, Math.min(outLines[L2].len, i2 * DS), tmp); arr[i2 * 2] = tmp[0]; arr[i2 * 2 + 1] = tmp[1]; }
    samples.push(arr);
  }

  // --- stations --------------------------------------------------------------
  var hubSet = {};
  for (var h1 = 0; h1 < hubs.length; h1++) hubSet[hubs[h1]] = h1;
  var stations = [];       // {x, y, line, s, kind (0 stop, 1 interchange, 2 terminus), hub}
  var hubLines = hubs.map(function () { return []; });
  for (var L3 = 0; L3 < outLines.length; L3++) {
    var rt3 = outLines[L3].route, turnAt = {};
    for (var q4 = 1; q4 < rt3.length - 1; q4++) if (rt3[q4 + 1].d !== rt3[q4].d) turnAt[q4] = true;
    var sAcc = 0, gap = 4 + Math.floor(rnd() * 3), since = 0;
    // arc length along the ROUNDED path at each node: project node onto the samples (nearest)
    var smp = samples[L3];
    function sOfNode(nd) {
      var x = px(nd), y = py(nd), best = 1e9, bi = 0;
      for (var k3 = 0; k3 < smp.length / 2; k3++) { var dd2 = (smp[k3 * 2] - x) * (smp[k3 * 2] - x) + (smp[k3 * 2 + 1] - y) * (smp[k3 * 2 + 1] - y); if (dd2 < best) { best = dd2; bi = k3; } }
      return { s: Math.min(outLines[L3].len, bi * DS), off: Math.sqrt(best) };
    }
    void sAcc;
    for (var q5 = 0; q5 < rt3.length; q5++) {
      var nd5 = rt3[q5].n, isEnd = q5 === 0 || q5 === rt3.length - 1, hub = hubSet[nd5];
      since++;
      if (hub !== undefined) {
        var sh = sOfNode(nd5);
        stations.push({ x: px(nd5), y: py(nd5), line: L3, s: sh.s, kind: 1, hub: hub });
        hubLines[hub].push(L3);
        since = 0;
      } else if (isEnd) {
        stations.push({ x: px(nd5), y: py(nd5), line: L3, s: q5 === 0 ? 0 : outLines[L3].len, kind: 2, hub: -1 });
        since = 0;
      } else if (since >= gap && !turnAt[q5] && !turnAt[q5 - 1] && !turnAt[q5 + 1] && usedNode[nd5] === outLines[L3].id) {
        var other = false;
        for (var d5 = 0; d5 < 8; d5++) { var m5 = neighbour(nd5, d5); if (m5 >= 0 && usedNode[m5] >= 0 && usedNode[m5] !== outLines[L3].id) other = true; }
        if (!other) { var ss = sOfNode(nd5); if (ss.off < 0.25 * P) { stations.push({ x: px(nd5), y: py(nd5), line: L3, s: ss.s, kind: 0, hub: -1 }); since = 0; gap = 4 + Math.floor(rnd() * 3); } }
      }
    }
  }

  // --- the cell grid for the full-screen pass -------------------------------
  var CS = o.cellSize, CX = Math.ceil(2 * A / CS), CY = Math.ceil(2 / CS);
  var lists = [];
  for (var c1 = 0; c1 < CX * CY; c1++) lists.push([]);
  var reachLine = o.lineReachPx * unit;
  function primDistToBox(pr, x0, y0, x1, y1) {
    // conservative: distance from the box centre to the prim minus the half diagonal
    var mxx = (x0 + x1) / 2, myy = (y0 + y1) / 2, hd = Math.hypot(x1 - x0, y1 - y0) / 2, dd;
    if (pr[0] === 0) {
      var ax = pr[2], ay = pr[3], bx2 = pr[4], by2 = pr[5], abx = bx2 - ax, aby = by2 - ay, l2 = abx * abx + aby * aby;
      var h = l2 > 0 ? Math.max(0, Math.min(1, ((mxx - ax) * abx + (myy - ay) * aby) / l2)) : 0;
      dd = Math.hypot(mxx - ax - abx * h, myy - ay - aby * h);
    } else {
      dd = Math.abs(Math.hypot(mxx - pr[2], myy - pr[3]) - pr[4]);   // to the full circle: conservative
    }
    return dd - hd;
  }
  for (var k4 = 0; k4 < prims.length; k4++) {
    for (var j4 = 0; j4 < CY; j4++) for (var i4 = 0; i4 < CX; i4++) {
      var x0c = -A + i4 * CS, y0c = -1 + j4 * CS;
      if (primDistToBox(prims[k4], x0c, y0c, x0c + CS, y0c + CS) <= reachLine) lists[j4 * CX + i4].push(k4);
    }
  }
  var maxList = 0;
  for (var c2 = 0; c2 < lists.length; c2++) maxList = Math.max(maxList, lists[c2].length);
  var primData = new Float32Array(prims.length * 8);
  for (var k5 = 0; k5 < prims.length; k5++) {
    var p5 = prims[k5];
    // texel 0: a.x a.y b.x b.y (segment) or c.x c.y r a0 (arc); texel 1: sweep, s0, line, type + len / 1000
    primData.set([p5[2], p5[3], p5[4], p5[5], p5[6], p5[7], p5[1], p5[0]], k5 * 8);
  }
  var primLen = new Float32Array(prims.length);
  for (var k6 = 0; k6 < prims.length; k6++) primLen[k6] = prims[k6][8];
  var cellW = Math.max(1, maxList);
  var cellData = new Float32Array(cellW * CX * CY).fill(-1);
  for (var c3 = 0; c3 < lists.length; c3++) for (var e3 = 0; e3 < lists[c3].length; e3++) cellData[c3 * cellW + e3] = lists[c3][e3];

  return {
    P: P, R: R, NX: NX, NY: NY,
    lines: outLines.map(function (l) { return { id: l.id, len: l.len, hubs: l.hubs }; }),
    prims: primData, primLen: primLen, primCount: prims.length,
    cells: cellData, cellW: cellW, CX: CX, CY: CY, CS: CS,
    samples: samples, ds: DS,
    stations: stations,
    hubs: hubs.map(function (n, i) { return { x: px(n), y: py(n), lines: hubLines[i] }; }),
    work: aWork,
    lenient: lenientUsed,         // lines that needed a hairpin
    blockedCount: blocked.reduce(function (a, b) { return a + b; }, 0)
  };
}

// The plan for a track: the same seed always gives the same plan. When the
// frame is cramped (the kiosk's 400 px, a portrait screen) a seed can leave
// too few lines or interchanges; derived seeds (seed + k) are tried in
// order and the best plan kept: still deterministic.
export function generateMetroBest(o) {
  // R17's budget, counted in A* expansions, not milliseconds: a clock would
  // make the plan depend on the machine's speed and load, and the same track
  // must always give the same map
  var best = null, bestScore = -1, work = 0, budget = o.budgetWork || 1e12;
  for (var k = 0; k < 8; k++) {
    if (best && work > budget) break;
    var oo = {};
    for (var key in o) oo[key] = o[key];
    oo.seed = ((o.seed >>> 0) + k * 7919) >>> 0;
    var r = generateMetro(oo);
    work += r.work;
    var inter = 0;
    for (var h = 0; h < r.hubs.length; h++) {
      var seen = {}, c = 0;
      for (var q = 0; q < r.hubs[h].lines.length; q++) if (!seen[r.hubs[h].lines[q]]) { seen[r.hubs[h].lines[q]] = 1; c++; }
      if (c >= 2) inter++;
    }
    // a hairpin costs more than an interchange, less than a line
    var score = Math.min(r.lines.length, o.lines) * 10 + Math.min(inter, 6) - 7 * r.lenient;
    r.interchanges = inter; r.attempt = k;
    if (score > bestScore) { bestScore = score; best = r; }
    if (inter >= 4 && r.lines.length >= o.lines && r.lenient === 0) break;
  }
  return best;
}
